import { useTranslation } from "react-i18next";
import { format } from "date-fns";
//
import { useParams } from "react-router-dom";

import { WorkspaceSwitcher } from "@/features/workspaces/components/widget/workspace-switcher.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { SidebarTrigger } from "@/components/ui/sidebar.tsx";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";
import DisclaimerBox, { DisclaimerItem } from "@/components/DisclaimerBox";
import PATHS from "@/routes/paths";
import useUserStore from "@/features/auth/store/useUserStore";

export default function DashboardHeader() {
  const isMobile = useIsMobile();
  const { workspaceId } = useParams();
  const { t, i18n } = useTranslation(["workspaces", "calendar"]);
  const { data: workspaceInfo } = useWorkspaceInformationStore();
  const { user } = useUserStore();

  const isBlocked = workspaceInfo?.is_workspace_blocked_for_user;
  const hasLeft = workspaceInfo?.has_user_left_workspace;
  const knowledgeCut = workspaceInfo?.knowledge_cut;

  const showDisclaimer = (isBlocked || hasLeft) && knowledgeCut && workspaceId;

  const disclaimers: DisclaimerItem[] = [];

  if (showDisclaimer) {
    const title = isBlocked
      ? t("workspaces:headerDisclaimer.blocked")
      : t("workspaces:headerDisclaimer.left");
    const description = knowledgeCut
      ? t("workspaces:headerDisclaimer.dataLimitCalendar", {
          knowledge_cut: format(new Date(knowledgeCut), "PPP", {
            locale: getDateFnsLocale(i18n.language),
          }),
        })
      : undefined;

    disclaimers.push({
      id: "workspace-status",
      tone: "danger",
      icon: isBlocked ? "mdi:block-helper" : "mdi:alert",
      title,
      description,
    });
  }

  // Google Calendar reauth notice in header (for tutors only)
  if (user?.role === "tutor" && user?.specialRoleChecks?.google_calendar === "requires_reauth") {
    disclaimers.push({
      id: "google-reauth",
      tone: "warning",
      icon: "mdi:sync-alert",
      title: t("calendar:settings.googleReauthDisclaimerTitle", {
        defaultValue: "Google Reauthentication Needed",
      }),
      description: t("calendar:settings.googleReauthDisclaimerDescription", {
        defaultValue:
          "We detected a problem with your Google Calendar integration. Please reauthenticate to restore synchronization.",
      }),
      href: PATHS.TUTOR.SCHEDULE.SETTINGS,
    });
  }

  return (
    <header className="flex h-14 max-h-14 shrink-0 items-center sm:gap-2">
      <div className="mx-2 my-2 flex items-center justify-between">
        {isMobile && <SidebarTrigger />}
        <WorkspaceSwitcher triggerClassName={"max-sm:hidden"} />
      </div>

      {disclaimers.length > 0 ? <DisclaimerBox items={disclaimers} /> : null}
    </header>
  );
}
